import type {
  UploadFile,
  UploadState,
} from '@pflegenavi/shared-frontend/platform';
import {
  makeApiProvider,
  useApiQuery,
  useUploadFile,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type { IServiceProviderReceiptUploadApi } from './api';
import { ServiceProviderReceiptUploadApi } from './api';
import type { UseQueryResult } from '@tanstack/react-query';
import { replaceEqualDeep } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import type { ServiceProviderReceiptUploadControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';

const ApiContext = createContext<IServiceProviderReceiptUploadApi | undefined>(
  undefined
);
const {
  useApi: useServiceProviderReceiptUploadApi,
  ApiProvider: ServiceProviderReceiptUploadApiProvider,
} = makeApiProvider({
  name: 'ServiceProviderReceiptUpload',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ServiceProviderReceiptUploadApi(tenantId, auth, apiUrl),
});

export { ServiceProviderReceiptUploadApiProvider };

export const useGetReceiptImagesWithTenant = ({
  tenant,
}: {
  tenant: string;
}): ((receiptImageIds?: string[]) => UseQueryResult<
  | {
      [p: string]: string | undefined;
    }
  | undefined,
  unknown
>) => {
  return (receiptImageIds: string[] | undefined) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useReceiptImages(tenant, receiptImageIds);
};

const getReceiptImagesKey = (id?: string) => [`receipt-images-multi`, id];
const RECEIPT_IMAGE_URLS_AND_TYPE_KEY = (imageIds?: string[]) => [
  'RECEIPT_IMAGE_URLS_AND_TYPE_KEY',
  { imageIds },
];

export const useReceiptImages = (
  tenant: string,
  receiptImageIds?: string[]
): UseQueryResult<
  | {
      [key: string]: string | undefined;
    }
  | undefined,
  unknown
> => {
  return useApiQuery(
    useServiceProviderReceiptUploadApi,
    getReceiptImagesKey(receiptImageIds?.toString()),
    (api) => {
      if (receiptImageIds && receiptImageIds?.length > 0) {
        return api.getReceiptUrls({
          body: {
            tenant,
            receiptIds: receiptImageIds,
          },
        });
      }
      return Promise.resolve(undefined);
    },
    {
      enabled: receiptImageIds !== undefined && receiptImageIds?.length !== 0,
      gcTime: 0,
      refetchOnWindowFocus: false,
      structuralSharing: (oldData, newData) =>
        isEqual(oldData, newData)
          ? oldData
          : replaceEqualDeep(oldData, newData),
    }
  );
}; /*

const GET_RECEIPT_URLS_KEY = ['get-receipt-urls'];

export const useGetReceiptUrls = ({
  tenant,
}: {
  tenant: string;
}): UseMutationResult<
  | ServiceProviderReceiptUploadControllerResultTypes['getReceiptUrls']
  | undefined,
  unknown,
  {
    receiptImageIds: string[];
  }
> => {
  const api = useServiceProviderReceiptUploadApi();
  const result = useMutation<
    | ServiceProviderReceiptUploadControllerResultTypes['getReceiptUrls']
    | undefined,
    unknown,
    {
      receiptImageIds: string[];
    }
  >(GET_RECEIPT_URLS_KEY, (data) =>
    api.getReceiptUrls({
      body: {
        tenant,
        receiptIds: data.receiptImageIds,
      },
    })
  );
  return result;
};*/

interface UseUploadReceiptImageProps {
  tenant: string;
  uuid: () => string;
  computeMd5Hash: (file: File) => Promise<string>;
}

export const useGetUploadUrl: ({
  tenant,
}: {
  tenant: string;
}) => (
  receiptId: string,
  fileType: string,
  md5Hash: string
) => Promise<
  ServiceProviderReceiptUploadControllerResultTypes['getUploadUrl']
> = ({ tenant }: { tenant: string }) => {
  const api = useServiceProviderReceiptUploadApi();
  return useCallback(
    (receiptId: string, fileType: string, md5Hash: string) => {
      return api.getUploadUrl({
        params: {
          imageId: receiptId,
        },
        body: {
          tenant,
          fileType,
          md5Hash,
        },
      });
    },
    [api, tenant]
  );
};

export const useUploadReceiptImage = ({
  tenant,
  uuid,
  computeMd5Hash,
}: UseUploadReceiptImageProps): {
  upload: UploadFile;
  uploadState: UploadState | undefined;
} => {
  const api = useServiceProviderReceiptUploadApi();

  const getUploadUrl = useCallback(
    (receiptId: string, fileType: string, md5Hash: string) => {
      return api.getUploadUrl({
        params: {
          imageId: receiptId,
        },
        body: {
          tenant,
          fileType,
          md5Hash,
        },
      });
    },
    [api, tenant]
  );

  const { upload, uploadState } = useUploadFile({
    uuid,
    getUploadUrl,
    computeMd5Hash,
  });

  return {
    upload,
    uploadState,
  };
};

export const useReceiptImageUrlsAndType = (
  imageIds: string[] | undefined,
  tenant: string
): UseQueryResult<
  Array<{
    imageId: string;
    imageUri?: string;
    fileType?: string;
  }>,
  unknown
> => {
  return useApiQuery(
    useServiceProviderReceiptUploadApi,
    RECEIPT_IMAGE_URLS_AND_TYPE_KEY(imageIds),
    async (api) => {
      if (imageIds && imageIds?.length > 0) {
        const imageIdsToUris = await api.getReceiptUrls({
          body: {
            receiptIds: imageIds,
            tenant,
          },
        });
        const imageIdsToTypes = await Promise.all(
          imageIds.map(async (imageId) => {
            const imageUri = imageIdsToUris?.[imageId];
            if (!imageUri) {
              return {
                imageId,
                imageUri: undefined,
                fileType: undefined,
              };
            }

            const response = await fetch(imageUri);
            const extractedFileType =
              response.headers.get('content-type') ?? 'type-unknown';

            return {
              imageId,
              imageUri,
              fileType: extractedFileType,
            };
          })
        );

        return imageIdsToTypes;
      }
      return null;
    }
  );
};
